import Select from 'react-select';
import CheckboxField from '../../../../components/Form/components/CheckboxField/CheckboxField';
import { useAppSelector } from '../../../../store/hooks';
import { selectCurrentLang } from '../../../../store/selectors/appSelectors';
import { BroadcastersOverlayPlayerQueryI } from '../../../../pages/projects/ViewVideoProject/models';
import styles from './styles.module.scss';
import { selectCurrentUser } from '../../../../store/selectors/authSelectors';
import { AccessLevelE, TooltipPlaceE, UserRoleE } from '../../../../models/enums';
import Tooltip from '../../../../components/Tooltip/Tooltip';
import { broadcasterSelectStyles } from './helpers/selectStyles';

interface BroadcasterLogoI {
    isLogoVisible: boolean
    setIsLogoVisible: (isLogoVisible: boolean) => void
    isClockVisible: boolean
    setIsClockVisible: (isClockVisible: boolean) => void
    broadcastersOverlayPlayerData: BroadcastersOverlayPlayerQueryI
    setCurrentBroadcaster: (currentBroadcaster: { value: number, label: string } | undefined) => void
    currentBroadcaster: { value: number, label: string } | undefined
    isSafeZoneVisible: boolean
    setIsSafeZoneVisible: (isSafeZoneVisible: boolean) => void
}

export const BroadcasterLogo = ({
    isLogoVisible,
    setIsLogoVisible,
    isClockVisible,
    setIsClockVisible,
    broadcastersOverlayPlayerData,
    setCurrentBroadcaster,
    currentBroadcaster,
    isSafeZoneVisible,
    setIsSafeZoneVisible
}: BroadcasterLogoI) => {
    const lang = useAppSelector(selectCurrentLang);
    const handleLogoChange = (state: boolean) => {
        setIsLogoVisible(state);
    };
    const user = useAppSelector(selectCurrentUser);

    const isAdmin = user?.accessLevel === AccessLevelE.Admin && user?.role?.includes(UserRoleE.Broadcaster_Admin);

    const handleClockChange = (state: boolean) => {
        setIsClockVisible(state);
    };

    const filteredBroadcasters = broadcastersOverlayPlayerData?.Broadcasters?.filter((broadcaster) => broadcaster.BroadcasterLogo.ImageSource || broadcaster.ClockLogo.ImageSource);

    if (!filteredBroadcasters?.length && !isAdmin) return null;

    if (!filteredBroadcasters?.length && isAdmin) return null;

    const isLogoDisabled = !filteredBroadcasters?.some((broadcaster) => broadcaster?.Id === currentBroadcaster?.value && broadcaster?.BroadcasterLogo?.ImageSource);
    const isClockDisabled = !filteredBroadcasters?.some((broadcaster) => broadcaster?.Id === currentBroadcaster?.value && broadcaster?.ClockLogo?.ImageSource);

    return (
        <>
                <div className={styles.logoRow}>
                    <div className={styles.controlItem}>
                        <Tooltip text="The broadcaster logo is not set for this broadcaster" place={TooltipPlaceE.TOP} isShowTooltip={isLogoDisabled}>
                            <CheckboxField
                                name="logo"
                                label="Show logo"
                                defaultValue={isLogoVisible}
                                onChange={handleLogoChange}
                                style={{
                                    filter: isLogoDisabled ? "grayscale(1) opacity(0.5)" : "none",
                                }}
                                props={{
                                    checked: isLogoVisible,
                                    disabled: isLogoDisabled,
                                    className: isLogoDisabled ? styles.disabledCheckbox : "",
                                }}
                            />
                        </Tooltip>
                    </div>
                    {isLogoVisible && (
                        <div className={styles.compactSelectWrapper}>
                            <Select
                                options={filteredBroadcasters?.map((broadcaster) => ({
                                    value: broadcaster.Id,
                                    label: broadcaster.Name,
                                }))}
                                value={currentBroadcaster}
                                onChange={(option) => setCurrentBroadcaster(option)}
                                isDisabled={!broadcastersOverlayPlayerData?.AllowSelection}
                                styles={broadcasterSelectStyles(lang)}
                                placeholder="Select a broadcaster..."
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                blurInputOnSelect={false}
                                isSearchable={false}
                                classNamePrefix="video-player-broadcaster-select"
                            />
                        </div>
                    )}
                </div>
                <div className={styles.controlItem}>
                    <Tooltip text="The clock logo is not set for this broadcaster" place={TooltipPlaceE.TOP} isShowTooltip={isClockDisabled}>
                        <CheckboxField
                            name="clock"
                            label="Show clock"
                            defaultValue={isClockVisible}
                            onChange={handleClockChange}
                            style={{
                                height: "unset",
                                filter: isClockDisabled ? "grayscale(1) opacity(0.5)" : "none",
                            }}
                            props={{
                                checked: isClockVisible,
                                disabled: isClockDisabled,
                                className: isClockDisabled ? styles.disabledCheckbox : "",
                            }}
                        />
                    </Tooltip>
                </div>
                <div className={styles.controlItem}>
                    <CheckboxField
                        name="safe-zone"
                        label="Show safe zone"
                        defaultValue={isSafeZoneVisible}
                        onChange={setIsSafeZoneVisible}
                        style={{
                            height: "unset",
                        }}
                        props={{
                            checked: isSafeZoneVisible,
                        }}
                    />
                </div>
        </>
    );
};

